import { StripeInvoice, StripeInvoiceSubSyncEvent } from '../../../../../back-end/utilities/apiDefinitions'
import { SubSyncEventStripeEventType, SubSyncEventType } from '../../../constants/subSyncEvent'
import { OverviewComponentFrameProps } from './overview'
import * as I from '../../../utilities/me'
import moment from 'moment'
import { DetailedStripeCharge, DetailedStripePayout } from '../../../screens/subsync/subsync'
import { StripeEvent } from '../../../../../back-end/common/stripeEvent'

type OverflowMenuOptions = Pick<OverviewComponentFrameProps, 'historyView' | 'creditNotesEnabled' | 'stripeFeesBillsEnabled' | 'appState'> &
	Partial<Pick<OverviewComponentFrameProps, 'retryFailedInvoiceTransaction' | 'retryFailedEvent'>>

const getInvoiceOverflowMenuOptions = (item: StripeInvoice, props: OverflowMenuOptions) => {
	const invoice = props.historyView.events.find((invoice) => invoice.stripeInvoiceID === item.stripeInvoiceID)
	const originalInvoiceEvent = getOriginalInvoiceEvent(invoice?.events)
	const disableLinks = originalInvoiceEvent?.xeroOrg && originalInvoiceEvent?.xeroInvoiceID ? false : true
	const paidEvent = getPaidInvoiceEvent(invoice?.events)
	const creditNoteEvents = getCreditNoteInvoiceEvents(invoice?.events) ?? []

	return [
		{ name: 'View Stripe Invoice', method: (id: string) => handleViewStripeInvoice(id, props), disabled: false },
		{ name: 'View Xero Invoice', method: (id: string) => handleViewXeroInvoice(id, props), disabled: disableLinks },
	]
		.concat(
			I.have('admin', props.appState.permission[props.appState.context]) && invoice && props.retryFailedInvoiceTransaction
				? getRetryInvoiceOption(invoice, props)
				: []
		)
		.concat(
			props.stripeFeesBillsEnabled && paidEvent
				? [
						{
							name: 'View Xero Bill (Stripe Fee)',
							method: (id: string) => handleViewXeroBillForInvoice(id, props),
							disabled: !paidEvent?.xeroInvoiceID,
						},
				  ]
				: []
		)
		.concat(
			props.creditNotesEnabled && creditNoteEvents.length > 0
				? [
						{
							name: 'View Stripe Credit Note',
							method: (id: string) => handleViewStripeCreditNote(id, props),
							disabled: !creditNoteEvents?.some((event) => event.stripeInvoiceStripeID),
						},
						{
							name: 'View Xero Credit Note',
							method: (id: string) => handleViewXeroCreditNote(id, props),
							disabled: !creditNoteEvents?.some((event) => event.xeroInvoiceID && event.result === 'success'),
						},
				  ]
				: []
		)
}

const getPayoutOverflowMenuOptions = (item: DetailedStripePayout, props: OverflowMenuOptions) => {
	const latestTransaction = item.events![0]?.transactions?.[0]
	return [
		{ name: 'View Stripe Payout', method: (id: string) => handleViewStripePayout(id, props), disabled: false },
		{ name: 'View Xero Invoice', method: (id: string) => handleViewXeroPayoutInvoice(id, props), disabled: !latestTransaction || !item.xeroOrg },
	].concat(
		I.have('admin', props.appState.permission[props.appState.context]) &&
			latestTransaction &&
			latestTransaction.result !== 'success' &&
			props.retryFailedEvent
			? [{ name: 'Retry Transaction', method: (id: string) => handleRetryPayoutTransaction(id, props), disabled: false }]
			: []
	)
}

const getPaymentOverflowMenuOptions = (item: DetailedStripeCharge, props: OverflowMenuOptions) => {
	const latestOriginalEventTransaction = getOriginalChargeEvent(item.events)?.transactions?.[0]
	const latestTransaction = item.events![0]?.transactions?.[0]
	const refundTransaction = item.events?.find((event) => event.type === 'charge.refunded')
	return [
		{ name: 'View Stripe Payment', method: (id: string) => handleViewStripeCharge(id, props), disabled: false },
		{
			name: 'View Xero Invoice',
			method: (id: string) => handleViewXeroPaymentInvoice(id, props),
			disabled: !latestOriginalEventTransaction?.xeroInvoiceID || !item.xeroOrg,
		},
	]
		.concat(
			props.stripeFeesBillsEnabled
				? [
						{
							name: 'View Xero Bill (Stripe Fee)',
							method: (id: string) => handleViewXeroBillForPayment(id, props),
							disabled: !latestOriginalEventTransaction?.xeroBillID,
						},
				  ]
				: []
		)
		.concat(
			refundTransaction
				? [
						{
							name: 'View Xero Credit Note',
							method: (id: string) => handleViewXeroCreditNoteForPayment(id, props),
							disabled: !refundTransaction.transactions?.[0]?.xeroCreditNoteID || refundTransaction.transactions?.[0]?.result !== 'success',
						},
				  ]
				: []
		)
		.concat(
			I.have('admin', props.appState.permission[props.appState.context]) &&
				latestTransaction &&
				latestTransaction.result !== 'success' &&
				props.retryFailedEvent
				? [{ name: 'Retry Transaction', method: (id: string) => handleRetryPaymentTransaction(id, props), disabled: false }]
				: []
		)
}

// could be an invoice.finalized event, or an invoice.paid event that we treated as a created event
const getOriginalInvoiceEvent = (events: StripeInvoiceSubSyncEvent[] | undefined) =>
	events?.find((event) => event.type === SubSyncEventType.Created || event.type === SubSyncEventType.UpdatedNew) ||
	// only fall through to paid type events if there are no created ones - this means we received finalized+paid together, ignored finalized, and there was an error before we could generate a simulated created event
	events?.find((event) => event.type === SubSyncEventType.Paid)

// an invoice.paid event (from Stripe, not Xero) that *wasn't* paid wholly by credit note - i.e. must have involved money
const getPaidInvoiceEvent = (events: StripeInvoiceSubSyncEvent[] | undefined): StripeInvoiceSubSyncEvent | undefined =>
	// for invoices paid by credit note we don't want to show any event at all in the paid column - including error messages - as it isn't relevant to Stripe fees and there's no paid amount
	events?.some((event) => event.stripeEventType === SubSyncEventStripeEventType.InvoicePaid && event.message?.includes('Paid by credit note'))
		? undefined
		: events?.find((event) => event.stripeEventType === SubSyncEventStripeEventType.InvoicePaid)

// any events related to credit notes from either Stripe or Xero
const getCreditNoteInvoiceEvents = (events: StripeInvoiceSubSyncEvent[] | undefined) => events?.filter((event) => event.type === SubSyncEventType.Credit)

// most recent version of original event
const getOriginalChargeEvent = (events: StripeEvent[] | undefined) => events?.find((event) => event.type === 'charge.succeeded')

// This excludes events where we don't actually do any processing, so they will always be marked success
const getMostRecentRelevantEvent = (events: StripeInvoiceSubSyncEvent[] | undefined) =>
	events?.find((event) => !['Paid by credit note', 'No action taken'].includes(event.message || '')) || events?.[0]

const handleViewStripeInvoice = async (invoiceID: string, props: OverflowMenuOptions) => {
	const invoice = props.historyView.events.find((invoice) => invoice.stripeInvoiceID === invoiceID)
	const originalInvoiceEvent = getOriginalInvoiceEvent(invoice!.events)
	// we already know all of the correct events exist or these options wouldn't be accessible via the overflow menu
	window.open(`https://dashboard.stripe.com/invoices/${originalInvoiceEvent!.stripeInvoiceStripeID}`, 'StripeInvoiceTab')
}

const handleViewStripePayout = async (payoutID: string, props: OverflowMenuOptions) => {
	const payout = props.historyView.payouts.find((payout) => payout.stripePayoutID === payoutID)
	window.open(`https://dashboard.stripe.com/payouts/${payout!.stripeID}`, 'StripePayoutTab')
}

const handleViewStripeCharge = async (chargeID: string, props: OverflowMenuOptions) => {
	const payment = props.historyView.payments.find((payment) => payment.stripeChargeID === chargeID)
	window.open(`https://dashboard.stripe.com/payments/${payment!.stripePaymentIntentID}`, 'StripeChargeTab')
}

// events that have a invoiceFinalisedTs
const getLatestInvoiceFinalisedTs = (events: StripeInvoiceSubSyncEvent[] | undefined): StripeInvoiceSubSyncEvent | undefined => {
	const sortedEvents = events
		?.filter((event) => event.invoiceFinalisedTs)
		.sort((a, b) => (moment(b.invoiceFinalisedTs).isAfter(moment(a.invoiceFinalisedTs)) ? 1 : -1))

	// Return the event with the most recent invoiceFinalisedTs
	return sortedEvents && sortedEvents.length !== 0 ? sortedEvents[0] : undefined
}

const handleViewXeroInvoice = async (invoiceID: string, props: OverflowMenuOptions) => {
	const invoice = props.historyView.events.find((invoice) => invoice.stripeInvoiceID === invoiceID)
	const originalInvoiceEvent = getOriginalInvoiceEvent(invoice!.events)

	window.open(
		`https://go.xero.com/organisationlogin/default.aspx?shortcode=${
			originalInvoiceEvent!.xeroOrg!.shortCode
		}&redirecturl=/AccountsReceivable/View.aspx?InvoiceID=${originalInvoiceEvent!.xeroInvoiceID}`,
		'XeroInvoiceTab'
	)
}
const handleViewXeroPayoutInvoice = async (payoutID: string, props: OverflowMenuOptions) => {
	const payout = props.historyView.payouts.find((payout) => payout.stripePayoutID === payoutID)
	const latestEvent = payout!.events![0]?.transactions?.[0]

	window.open(
		`https://go.xero.com/organisationlogin/default.aspx?shortcode=${
			payout!.xeroOrg!.xeroOrg!.shortCode
		}&redirecturl=/AccountsReceivable/View.aspx?InvoiceID=${latestEvent!.xeroInvoiceID}`,
		'XeroInvoiceTab'
	)
}
const handleViewXeroPaymentInvoice = async (chargeID: string, props: OverflowMenuOptions) => {
	const payment = props.historyView.payments.find((payment) => payment.stripeChargeID === chargeID)
	const latestEvent = payment!.events![0]?.transactions?.[0]

	window.open(
		`https://go.xero.com/organisationlogin/default.aspx?shortcode=${
			payment!.xeroOrg!.xeroOrg!.shortCode
		}&redirecturl=/AccountsReceivable/View.aspx?InvoiceID=${latestEvent!.xeroInvoiceID}`,
		'XeroInvoiceTab'
	)
}
const handleViewStripeCreditNote = async (invoiceID: string, props: OverflowMenuOptions) => {
	const invoice = props.historyView.events.find((invoice) => invoice.stripeInvoiceID === invoiceID)
	const stripeCreditNoteEvent = getCreditNoteInvoiceEvents(invoice?.events)

	window.open(`https://dashboard.stripe.com/credit_notes/${stripeCreditNoteEvent![0].stripeInvoiceStripeID}`, 'StripeCreditNoteTab')
}

const handleViewXeroCreditNote = async (invoiceID: string, props: OverflowMenuOptions) => {
	const invoice = props.historyView.events.find((invoice) => invoice.stripeInvoiceID === invoiceID)
	const xeroCreditNoteEvent = getCreditNoteInvoiceEvents(invoice!.events)

	window.open(
		`https://go.xero.com/organisationlogin/default.aspx?shortcode=${
			xeroCreditNoteEvent![0].xeroOrg!.shortCode
		}&redirecturl=/AccountsReceivable/ViewCreditNote.aspx?CreditNoteID=${xeroCreditNoteEvent![0].xeroInvoiceID}`,
		'XeroCreditNoteTab'
	)
}

const handleViewXeroCreditNoteForPayment = async (chargeID: string, props: OverflowMenuOptions) => {
	const payment = props.historyView.payments.find((payment) => payment.stripeChargeID === chargeID)

	window.open(
		`https://go.xero.com/organisationlogin/default.aspx?shortcode=${
			payment!.xeroOrg!.xeroOrg!.shortCode
		}&redirecturl=/AccountsReceivable/ViewCreditNote.aspx?CreditNoteID=${payment!.events![0]!.transactions![0]!.xeroCreditNoteID}`,
		'XeroCreditNoteTab'
	)
}

const handleViewXeroBillForInvoice = async (invoiceID: string, props: OverflowMenuOptions) => {
	const invoice = props.historyView.events.find((invoice) => invoice.stripeInvoiceID === invoiceID)
	const paidEvent = getPaidInvoiceEvent(invoice!.events)

	window.open(
		`https://go.xero.com/organisationlogin/default.aspx?shortcode=${paidEvent!.xeroOrg!.shortCode}&redirecturl=/AccountsPayable/View.aspx?InvoiceID=${
			paidEvent!.xeroInvoiceID
		}`,
		'XeroBillTab'
	)
}

const handleViewXeroBillForPayment = async (chargeID: string, props: OverflowMenuOptions) => {
	const payment = props.historyView.payments.find((payment) => payment.stripeChargeID === chargeID)

	window.open(
		`https://go.xero.com/organisationlogin/default.aspx?shortcode=${
			payment!.xeroOrg!.xeroOrg!.shortCode
		}&redirecturl=/AccountsPayable/View.aspx?InvoiceID=${payment!.events![0]!.transactions![0]!.xeroBillID}`,
		'XeroBillTab'
	)
}

const handleRetryInvoiceTransaction = async (invoiceID: string, props: OverflowMenuOptions) => {
	const invoice = props.historyView.events.find((invoice) => invoice.stripeInvoiceID === invoiceID)
	const body = {
		stripeInvoiceID: invoiceID,
		xeroOrgID: invoice && invoice.events![0] ? invoice!.events![0].xeroOrg!.xeroOrgID : undefined,
		stripeOrgID: invoice!.stripeOrg!.stripeOrgID,
	}
	props.retryFailedInvoiceTransaction && props.retryFailedInvoiceTransaction(body)
}

const getRetryInvoiceOption = (invoice: StripeInvoice, props: OverflowMenuOptions) => {
	// an event could have been retried many times - we only want to check the status of the most recent attempt, and only allow retry if that attempt was error
	const mostRecentTransactionsPerEvent = invoice.events!.reduce((final, curr) => {
		const eventIndex = final.findIndex((prev) => prev.stripeEventID === curr.stripeEventID)
		if (eventIndex < 0) {
			final.push(curr)
		}
		return final
	}, [] as StripeInvoiceSubSyncEvent[])
	return mostRecentTransactionsPerEvent.some((event) => event.result === 'error')
		? [{ name: 'Retry Transaction', method: (id: string) => handleRetryInvoiceTransaction(id, props), disabled: false }]
		: []
}

const handleRetryPayoutTransaction = async (payoutID: string, props: OverflowMenuOptions) => {
	const payout = props.historyView.payouts.find((payout) => payout.stripePayoutID === payoutID)
	const latestTransaction = payout!.events![0]?.transactions?.[0]
	const body = {
		stripeEventID: latestTransaction!.stripeEventID!,
		stripeOrgID: payout!.stripeOrg!.stripeOrg!.stripeOrgID!,
	}
	props.retryFailedEvent && props.retryFailedEvent(body)
}

const handleRetryPaymentTransaction = async (chargeID: string, props: OverflowMenuOptions) => {
	const payment = props.historyView.payments.find((payment) => payment.stripeChargeID === chargeID)
	const latestTransaction = payment!.events![0]?.transactions?.[0]
	const body = {
		stripeEventID: latestTransaction!.stripeEventID!,
		stripeOrgID: payment!.stripeOrg!.stripeOrg!.stripeOrgID!,
	}
	props.retryFailedEvent && props.retryFailedEvent(body)
}

const renderChargeEventType = (eventType: string) => {
	switch (eventType) {
		case 'charge.succeeded':
			return 'Created'
		case 'charge.refunded':
			return 'Refunded'
	}
}

export {
	getInvoiceOverflowMenuOptions,
	getPayoutOverflowMenuOptions,
	getPaymentOverflowMenuOptions,
	getOriginalInvoiceEvent,
	getPaidInvoiceEvent,
	getLatestInvoiceFinalisedTs,
	getOriginalChargeEvent,
	renderChargeEventType,
	getMostRecentRelevantEvent,
}
export type { OverflowMenuOptions }
